/**
 * YourWeek — Week quote (ref 06-your-week-dark).
 *
 * Shown after the rider taps "See price" in the Book sheet. Prefilled
 * with the route + days from location.state OR from the rider's
 * existing plan (commute-summary). Live-quotes `/api/pricing/quote`
 * on every day-chip toggle so the total is real.
 *
 * Continue to payment routes to /checkout/plan (the existing Stripe
 * sheet — we don't replace it).
 *
 * Never hardcodes $84/$42. If the quote endpoint fails we show a soft
 * placeholder rather than a fake price.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, ArrowRight, Clock, User, ChevronRight } from "lucide-react";

import { api } from "@/shared/lib/api";
import { formatTime12, formatTimeWindow12 } from "@/shared/lib/format";

const DAY_KEYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function YourWeek() {
  const nav = useNavigate();
  const loc = useLocation();

  const seeded = loc.state || {};
  const [pickup, setPickup] = useState(seeded.pickup || "");
  const [destination, setDestination] = useState(seeded.destination || "");
  const [days, setDays] = useState(seeded.days || ["Mon", "Tue", "Wed", "Thu", "Fri"]);
  const [pickupWindow] = useState(seeded.pickupWindow || "07:15-07:30");
  const [seats, setSeats] = useState(seeded.seats || 1);
  const [distanceMiles, setDistanceMiles] = useState(seeded.distanceMiles || 0);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);

  // If not seeded, hydrate from the rider's existing plan.
  useEffect(() => {
    if (pickup && destination) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/me/commute-summary");
        if (cancelled) return;
        const p = data?.plan;
        if (p?.has_plan) {
          setPickup(p.pickup_address || "");
          setDestination(p.destination_address || "");
          setDays(p.days || days);
          setSeats(p.seats || 1);
          setDistanceMiles(p.distance_miles || 0);
        }
      } catch { /* keep placeholders */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  // Live-quote whenever days/seats/distance changes.
  useEffect(() => {
    if (!distanceMiles) { setQuote(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/pricing/quote", {
          params: {
            distance_miles: distanceMiles,
            days: days.length || 1,
            seats,
            ride_type: "shared",
            billing_cycle: "weekly",
          },
        });
        if (!cancelled) setQuote(data);
      } catch { if (!cancelled) setQuote(null); }
    })();
    return () => { cancelled = true; };
  }, [days.length, seats, distanceMiles]);

  const toggleDay = (k) => {
    setDays((cur) => (cur.includes(k) ? cur.filter((d) => d !== k) : [...cur, k].sort((a, b) => DAY_KEYS.indexOf(a) - DAY_KEYS.indexOf(b))));
  };

  const weekTotal = quote?.week_total ?? quote?.cycle_total;
  const firstWeekTotal = quote?.first_week_total ?? quote?.first_ride_week_total;
  const showsDiscount = firstWeekTotal != null && weekTotal != null && firstWeekTotal < weekTotal;

  return (
    <div
      data-dusk="1"
      data-testid="your-week"
      style={{ minHeight: "100vh", background: "var(--dusk-bg)", position: "relative" }}
    >
      {/* Header — wordmark + back arrow */}
      <div style={{ padding: "16px 18px", display: "flex", alignItems: "center", gap: 14 }}>
        <button
          type="button"
          onClick={() => nav("/book")}
          className="w-10 h-10 rounded-full inline-flex items-center justify-center"
          style={{ background: "var(--dusk-glass)", boxShadow: "var(--dusk-glass-border)", color: "var(--dusk-text)" }}
          data-testid="your-week-back"
          aria-label="Back"
        >
          <ArrowLeft size={18} />
        </button>
        <img src="/assets/link-full-logo.svg" alt="Link" style={{ height: 26 }} />
      </div>

      <div style={{ padding: "0 22px 140px" }}>
        <h1
          style={{
            fontFamily: "var(--font-display)", fontWeight: 800,
            fontSize: 36, letterSpacing: "-0.02em", color: "var(--dusk-text)",
            marginTop: 8,
          }}
        >
          YOUR WEEK
        </h1>
        <div style={{ color: "var(--dusk-text-muted)", fontSize: 15, marginTop: 8 }}>
          Plan your ride. Lock in your rate.
        </div>

        {/* Route + price card */}
        <div
          style={{
            marginTop: 22,
            background: "var(--dusk-glass)",
            backdropFilter: "var(--dusk-glass-blur)",
            WebkitBackdropFilter: "var(--dusk-glass-blur)",
            boxShadow: "var(--dusk-glass-border)",
            borderRadius: 18,
            padding: "18px 18px",
          }}
          data-testid="your-week-route-card"
        >
          <button
            type="button"
            onClick={() => nav("/book", { state: { pickup, destination, days } })}
            className="w-full flex items-center gap-3"
            style={{ background: "transparent", padding: 0, color: "var(--dusk-text)", textAlign: "left" }}
            data-testid="your-week-route-row"
          >
            <MapPin size={18} color="var(--dusk-cta)" />
            <div style={{ flex: 1, minWidth: 0, fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 15 }}>
              <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "inline-block", maxWidth: 120, verticalAlign: "middle" }}>
                {pickup || "Pickup"}
              </span>
              {" "}<ArrowRight size={14} style={{ display: "inline", verticalAlign: "middle", color: "var(--dusk-text-muted)" }} />{" "}
              <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "inline-block", maxWidth: 130, verticalAlign: "middle" }}>
                {destination || "Destination"}
              </span>
            </div>
            <ChevronRight size={18} color="var(--dusk-text-muted)" />
          </button>

          <div style={{ height: 1, background: "var(--dusk-hairline)", margin: "16px 0" }} />

          {loading ? (
            <div style={{ color: "var(--dusk-text-muted)", fontSize: 14 }}>Loading quote…</div>
          ) : quote ? (
            <div style={{ display: "flex", gap: 30 }}>
              {showsDiscount ? (
                <>
                  <div>
                    <div style={{ color: "var(--dusk-text-muted)", fontSize: 13 }}>First week</div>
                    <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 26, color: "var(--dusk-text)" }} data-testid="your-week-first-week">
                      ${Number(firstWeekTotal).toFixed(0)}/week
                    </div>
                  </div>
                  <div>
                    <div style={{ color: "var(--dusk-text-muted)", fontSize: 13 }}>Then</div>
                    <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 26, color: "var(--dusk-text)" }} data-testid="your-week-then">
                      ${Number(weekTotal).toFixed(0)}
                    </div>
                  </div>
                </>
              ) : (
                <div>
                  <div style={{ color: "var(--dusk-text-muted)", fontSize: 13 }}>This week</div>
                  <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 26, color: "var(--dusk-text)" }} data-testid="your-week-total">
                    ${Number(weekTotal || 0).toFixed(0)}/week
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={{ color: "var(--dusk-text-muted)", fontSize: 14 }} data-testid="your-week-no-quote">
              Add a pickup and destination to see your rate.
            </div>
          )}
        </div>

        {/* Day chips */}
        <div
          style={{
            marginTop: 20,
            background: "var(--dusk-glass)",
            backdropFilter: "var(--dusk-glass-blur)",
            WebkitBackdropFilter: "var(--dusk-glass-blur)",
            boxShadow: "var(--dusk-glass-border)",
            borderRadius: 18,
            padding: "16px 18px 18px",
          }}
        >
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 16, color: "var(--dusk-text)" }}>
            Select days
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 12 }} data-testid="your-week-day-chips">
            {DAY_KEYS.map((k) => {
              const active = days.includes(k);
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => toggleDay(k)}
                  className="flex-1 rounded-xl"
                  style={{
                    padding: "12px 0",
                    fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 15,
                    color: active ? "var(--dusk-text)" : "var(--dusk-text-muted)",
                    background: active ? "var(--dusk-cta)" : "rgba(255,255,255,0.04)",
                    boxShadow: active ? "var(--dusk-cta-shadow)" : "var(--dusk-glass-border)",
                  }}
                  data-testid={`your-week-day-${k}`}
                >
                  {k[0]}
                </button>
              );
            })}
          </div>
          <div style={{ marginTop: 10, color: "var(--dusk-text-muted)", fontSize: 13 }}>
            Rides reserved for selected days
          </div>
        </div>

        {/* Pickup + Seats rows */}
        <RowLink icon={Clock} label="Pickup" value={formatTimeWindow12(pickupWindow)} onClick={() => nav("/book")} testId="your-week-pickup-row" />
        <RowLink icon={User}  label="Seats"  value={String(seats)}                     onClick={() => setSeats((s) => (s >= 4 ? 1 : s + 1))} testId="your-week-seats-row" />

        {/* Continue to payment */}
        <button
          type="button"
          onClick={() => nav("/checkout/plan", { state: { pickup, destination, days, seats, distanceMiles } })}
          className="w-full inline-flex items-center justify-center gap-2 mt-6"
          style={{
            background: "var(--dusk-cta)", color: "var(--dusk-text)",
            fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 17,
            padding: "18px 20px", borderRadius: 40,
            boxShadow: "var(--dusk-cta-shadow)",
          }}
          data-testid="your-week-continue"
        >
          Continue to payment <ArrowRight size={18} />
        </button>

        <div style={{ marginTop: 16, textAlign: "center", color: "var(--dusk-text-muted)", fontSize: 12 }}>
          Flexible cancellation &middot; No hidden fees
        </div>
      </div>
    </div>
  );
}

function RowLink({ icon: Icon, label, value, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 mt-3"
      style={{
        background: "var(--dusk-glass)",
        backdropFilter: "var(--dusk-glass-blur)",
        WebkitBackdropFilter: "var(--dusk-glass-blur)",
        boxShadow: "var(--dusk-glass-border)",
        borderRadius: 16,
        padding: "14px 16px",
        color: "var(--dusk-text)", textAlign: "left",
      }}
      data-testid={testId}
    >
      <Icon size={18} color="var(--dusk-cta)" strokeWidth={1.9} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 15 }}>{label}</div>
        <div style={{ color: "var(--dusk-text-muted)", fontSize: 13 }}>{value}</div>
      </div>
      <ChevronRight size={16} color="var(--dusk-text-muted)" />
    </button>
  );
}
